import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateField,
  updateSeasonField,
  setLogo,
  setHeaderFiles,
  setSelectedClasses,
} from "./redux/masterDataSlice";
import {
  getMasterDataByUserId,
  getProposedClassList,
  submitMasterData,
  updateMasterData,
} from "./redux/masterDataThunk";
import userStore from "../../zustand/userStore"; // Assuming this path is correct
import "../../App.css"
import { setAppdAiUser } from "../../utils/appdAiHelpers";

const MONTHS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const CreateMaster = () => {
  const dispatch = useDispatch();
  const formData = useSelector((state) => state.masterData);
  const userData = userStore((state) => state.user);
  const proposedClassesList = useSelector((state) => state.masterData.proposedClassesList);
  const selectedClasses = useSelector((state) => state.masterData.selectedClasses);

  // Local state for validation and change detection
  const [validationErrors, setValidationErrors] = useState({});
  const [originalFormData, setOriginalFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);


  // useEffect(() => {
  //   if (userData) setAppdAiUser(userData, "demo");
  // }, []);

  useEffect(() => {
    dispatch(getMasterDataByUserId(userData.id));
    dispatch(getProposedClassList());
  }, [dispatch, userData.id]);


  useEffect(() => {
    if (!formData || !userData) return;

    // helper: check empty
    const isEmpty = (val) =>
      val === null || val === undefined || val === "";

    // 🔹 School basic info
    if (isEmpty(formData.schoolName) && userData.school_name) {
      dispatch(updateField({ field: "schoolName", value: userData.school_name }));
    }

    if (isEmpty(formData.address) && userData.address) {
      dispatch(updateField({ field: "address", value: userData.address }));
    }

    if (isEmpty(formData.email) && userData.email) {
      dispatch(updateField({ field: "email", value: userData.email }));
    }

    if (isEmpty(formData.contactNumber) && userData.contact_number) {
      dispatch(updateField({ field: "contactNumber", value: userData.contact_number }));
    }

    if (isEmpty(formData.altNumber) && userData.alt_number) {
      dispatch(updateField({ field: "altNumber", value: userData.alt_number }));
    }

    if (isEmpty(formData.website) && userData.website) {
      dispatch(updateField({ field: "website", value: userData.website }));
    }

    if (isEmpty(formData.schoolCategory) && userData.school_type) {
      dispatch(updateField({ field: "schoolCategory", value: userData.school_type.toLowerCase() }));
    }

    if (
      (formData.workingDays === null || formData.workingDays === "") &&
      userData.working_days
    ) {
      dispatch(updateField({ field: "workingDays", value: userData.working_days }));
    }

    // 🔹 Selected classes
    if (
      (!selectedClasses || selectedClasses.length === 0) &&
      userData.selected_classes?.length > 0
    ) {
      dispatch(setSelectedClasses(userData.selected_classes));
    }

    // 🔹 Logo (only if master data logo is empty)
    if (!formData.logo && userData.logo) {
      dispatch(setLogo(userData.logo));
    }

    // 🔹 Header files
    if (
      (!formData.headers || formData.headers.length === 0) &&
      userData.headers?.length > 0
    ) {
      dispatch(setHeaderFiles(userData.headers));
    }

  }, [formData, userData, dispatch, selectedClasses]);



  // Effect to capture initial data and check for changes
  useEffect(() => {
    // Set the original data once it's loaded from the API
    if (formData.isUpdate && formData.schoolName && !originalFormData) {
      setOriginalFormData(JSON.stringify({ ...formData, selectedClasses: selectedClasses }));
    }

    // Check for changes whenever Redux state updates
    if (originalFormData) {
      const currentFormData = JSON.stringify({ ...formData, selectedClasses: selectedClasses });
      setHasChanges(currentFormData !== originalFormData);
    }
  }, [formData, selectedClasses, originalFormData]);

  // --- Inline Styles ---
  const containerStyle = {
    maxWidth: "1000px",
    margin: "15px auto",
    padding: "15px 30px",
    backgroundColor: "rgb(123 47 247 / 51%)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    borderRadius: "12px",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
  };

  const labelStyle = {
    fontWeight: "600",
    fontSize: "14px",
    marginBottom: "6px",
    color: "#333",
    display: "block",
  };

  const inputStyle = {
    width: "120px",
    height: "32px",
    padding: "4px 8px",
    fontSize: "13px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  };

  const errorInputStyle = {
    border: '1px solid red',
  };



  const flexRowStyle = {
    display: "flex",
    gap: "20px",
    marginBottom: "15px",
    alignItems: "center",
  };

  const sectionHeaderStyle = {
    fontSize: "16px",
    fontWeight: "bold",
    marginBottom: "10px",
    color: "#444",
  };
  const checkboxContainerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    padding: '10px',
    backgroundColor: '#f5f5f5',
    maxHeight: '150px',
    overflowY: 'auto',
  };

  const checkboxItemStyle = {
    display: 'flex',
    alignItems: 'center',
    marginRight: '15px',
  };

  const checkboxInputStyle = {
    marginRight: '5px',
  };

  const errorTextStyle = {
    color: 'red',
    fontSize: '12px',
    marginTop: '5px',
  };
  // --- End Inline Styles ---

  // --- Handlers for general fields ---
  const handleInputChange = (season, field, value) => {
    dispatch(updateSeasonField({ season, field, value }));
  };

  const handleSchoolNameChange = (e) => {
    dispatch(updateField({ field: "schoolName", value: e.target.value }));
  };

  const handleAddressChange = (e) => {
    dispatch(updateField({ field: "address", value: e.target.value }));
  };

  const handleWebsiteChange = (e) => {
    dispatch(updateField({ field: "website", value: e.target.value }));
  };

  const handleEmailChange = (e) => {
    dispatch(updateField({ field: "email", value: e.target.value }));
  };

  const handleContactNumberChange = (e) => {
    dispatch(updateField({ field: "contactNumber", value: e.target.value }));
  };

  const handleAltNumberChange = (e) => {
    dispatch(updateField({ field: "altNumber", value: e.target.value }));
  };

  const handleSchoolCategoryChange = (e) => {
    dispatch(updateField({ field: "schoolCategory", value: e.target.value }));
  };

  const handleLogoChange = (e) => {
    dispatch(setLogo(e.target.files[0]));
  };

  const handleHeaderFilesChange = (e) => {
    dispatch(setHeaderFiles(Array.from(e.target.files)));
  };

  const handleWorkingDaysChange = (e) => {
    const value = parseInt(e.target.value);
    dispatch(updateField({ field: "workingDays", value: isNaN(value) ? "" : value }));
  };

  // --- Handler for proposed classes (multi-select) ---
  const handleSelectedClassesChange = (e) => {
    const { value, checked } = e.target;
    const id = parseInt(value);

    let updatedSelectedClasses = [...selectedClasses];

    if (checked) {
      if (!updatedSelectedClasses.includes(id)) {
        updatedSelectedClasses.push(id);
      }
    } else {
      updatedSelectedClasses = updatedSelectedClasses.filter(clsId => clsId !== id);
    }
    dispatch(setSelectedClasses(updatedSelectedClasses));
  };

  // --- Handler for Season Months ---
  const handleMonthToggle = (season, monthNumber) => {
    const currentMonths = formData[season].months || [];
    const updatedMonths = currentMonths.includes(monthNumber)
      ? currentMonths.filter((m) => m !== monthNumber)
      : [...currentMonths, monthNumber].sort((a, b) => a - b);
    dispatch(updateSeasonField({ season, field: "months", value: updatedMonths }));
  };

  // --- Handlers for Break Timings ---
  const handleBreakSlotsCountChange = (season, e) => {
    const count = parseInt(e.target.value) || 0;
    const currentBreakTimings = formData[season].breakTimings || [];
    let newBreakTimings = Array(count).fill(null).map((_, index) => ({
      start: currentBreakTimings[index]?.start || "",
      end: currentBreakTimings[index]?.end || "",
    }));
    dispatch(updateSeasonField({ season, field: "breakTimings", value: newBreakTimings }));
  };

  const handleBreakTimeChange = (season, index, field, value) => {
    const updatedBreakTimings = [...(formData[season].breakTimings || [])];
    updatedBreakTimings[index] = {
      ...updatedBreakTimings[index],
      [field]: value,
    };
    dispatch(updateSeasonField({ season, field: "breakTimings", value: updatedBreakTimings }));
  };

  // --- Validation Logic ---
  // --- Validation Logic ---
  const validateForm = () => {
    const errors = {};
    const { schoolName, address, schoolCategory, contactNumber, workingDays, email } = formData;

    const s = (val) => String(val || "").trim(); // safe trim helper

    if (!s(schoolName)) {
      errors.schoolName = "School name is required.";
    }
    if (!s(address)) {
      errors.address = "Address is required.";
    }
    if (!s(contactNumber)) {
      errors.contactNumber = "Contact number is required.";
    } else if (!/^[0-9]{10}$/.test(s(contactNumber))) {
      errors.contactNumber = "Contact number must be 10 digits.";
    }
    if (!s(email)) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s(email))) {
      errors.email = "Invalid email format.";
    }
    if (!s(schoolCategory)) {
      errors.schoolCategory = "School category is required.";
    }
    if (!workingDays || workingDays < 1 || workingDays > 7) {
      errors.workingDays = "Working days must be between 1 and 7.";
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Show all errors in alert
      alert(Object.values(errors).join("\n"));
      return false;
    }

    return true;
  };


  // --- Submission Logic ---
  const prepareFormData = () => {
    const formDataToSend = new FormData();

    formDataToSend.append("user_id", userData.id);
    formDataToSend.append("school_name", formData.schoolName);
    formDataToSend.append("address", formData.address);
    formDataToSend.append("website", formData.website || "");
    formDataToSend.append("email", formData.email || "");
    formDataToSend.append("contact_number", formData.contactNumber || "");
    formDataToSend.append("alt_number", formData.altNumber || "");
    formDataToSend.append("school_category", formData.schoolCategory);
    formDataToSend.append("selected_classes", JSON.stringify(selectedClasses));
    formDataToSend.append("working_days", formData.workingDays || "");

    formDataToSend.append(
      "summer_timings",
      JSON.stringify({
        schoolStart: formData.summer.schoolStart,
        schoolEnd: formData.summer.schoolEnd,
        assemblyStart: formData.summer.assemblyStart,
        assemblyEnd: formData.summer.assemblyEnd,
        breakTimings: formData.summer.breakTimings || [],
        months: formData.summer.months || [],
      })
    );
    formDataToSend.append(
      "winter_timings",
      JSON.stringify({
        schoolStart: formData.winter.schoolStart,
        schoolEnd: formData.winter.schoolEnd,
        assemblyStart: formData.winter.assemblyStart,
        assemblyEnd: formData.winter.assemblyEnd,
        breakTimings: formData.winter.breakTimings || [],
        months: formData.winter.months || [],
      })
    );

    if (formData.logo instanceof File) {
      formDataToSend.append("logo", formData.logo);
    }

    if (formData.headers && formData.headers.length > 0) {
      formData.headers.forEach((file) => {
        if (file instanceof File) {
          formDataToSend.append("header_files", file);
        }
      });
    }

    return formDataToSend;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    const formDataToSend = prepareFormData();
    try {
      await dispatch(submitMasterData(formDataToSend)).unwrap();
      alert("Data submitted successfully!");
      dispatch(getMasterDataByUserId(userData.id));
    } catch (err) {
      console.error("Submission failed:", err);
      alert("Submission failed: " + (err.message || "An unknown error occurred."));
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return;
    }
    const formDataToSend = prepareFormData();
    try {
      await dispatch(updateMasterData(formDataToSend)).unwrap();
      alert("Update successful!");
      dispatch(getMasterDataByUserId(userData.id));
    } catch (err) {
      console.error("Update failed:", err);
      alert("Update failed: " + (err.message || "An unknown error occurred."));
    }
  };

  return (
    <div style={containerStyle}>
      <div className="px-2">
        <h2 style={{ fontSize: "22px", fontWeight: "bold", marginBottom: "20px" }}>
          Master Data
        </h2>

        {formData.loading && <div style={{ color: "#007BFF", marginBottom: "15px" }}>Loading...</div>}
        {formData.error && <div style={{ color: "red", marginBottom: "15px" }}>Error: {formData.error}</div>}

        <form onSubmit={handleSubmit}>
          {/* School Name, Category, Working Days */}
          <div className="d-flex school-crt-form gap-2"  >
            <div style={{ marginBottom: "20px", width: "50%" }}>
              <label style={labelStyle} htmlFor="schoolName">School Name:</label>
              <input
                id="schoolName"
                type="text"
                value={formData.schoolName || ""}
                onChange={handleSchoolNameChange}
                style={{
                  ...inputStyle,
                  ...(validationErrors.schoolName && errorInputStyle),
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                }}
                placeholder="Enter school name"
                required
              />
              {validationErrors.schoolName && <div style={errorTextStyle}>{validationErrors.schoolName}</div>}
            </div>
            <div style={{ marginBottom: "20px", width: "25%" }}>
              <label style={labelStyle} htmlFor="schoolCategory">School Category:</label>
              <select
                id="schoolCategory"
                value={formData.schoolCategory || ""}
                onChange={handleSchoolCategoryChange}
                style={{
                  ...inputStyle,
                  ...(validationErrors.schoolCategory && errorInputStyle),
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                }}
                required
              >
                <option value="">Select category</option>
                <option value="primary">Primary School</option>
                <option value="secondary">Secondary School</option>
                <option value="preparatory">Preparatory</option>
                <option value="senior secondary">Senior Secondary</option>
              </select>
              {validationErrors.schoolCategory && <div style={errorTextStyle}>{validationErrors.schoolCategory}</div>}
            </div>
            <div style={{ marginBottom: "20px", width: "25%" }}>
              <label style={labelStyle}>Number of working Days in a week:</label>
              <input
                type="number"
                min={1}
                max={7}
                value={formData.workingDays ?? ""}
                onChange={handleWorkingDaysChange}
                style={{
                  ...inputStyle,
                  ...(validationErrors.workingDays && errorInputStyle),
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                }}
                placeholder="Enter days (1-7)"
                required
              />
              {validationErrors.workingDays && <div style={errorTextStyle}>{validationErrors.workingDays}</div>}
            </div>
          </div>

          {/* Address */}
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle} htmlFor="address">Address:</label>
            <textarea
              id="address"
              rows="1"
              value={formData.address || ""}
              onChange={handleAddressChange}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: validationErrors.address ? "1px solid red" : "1px solid #ccc",
                fontSize: "14px",
                resize: "vertical",
              }}
              placeholder="Enter school address"
              required
            ></textarea>
            {validationErrors.address && <div style={errorTextStyle}>{validationErrors.address}</div>}
          </div>

          {/* Website and Email */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle} htmlFor="website">Website:</label>
              <input
                id="website"
                type="url"
                value={formData.website || ""}
                onChange={handleWebsiteChange}
                style={{
                  ...inputStyle,
                  ...(validationErrors.website && errorInputStyle),
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                }}
                placeholder="https://example.com"
              />
              {validationErrors.website && <div style={errorTextStyle}>{validationErrors.website}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle} htmlFor="email">Email:</label>
              <input
                id="email"
                type="email"
                value={formData.email || ""}
                onChange={handleEmailChange}
                style={{
                  ...inputStyle,
                  ...(validationErrors.email && errorInputStyle),
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                }}
                placeholder="school@example.com"
              />
              {validationErrors.email && <div style={errorTextStyle}>{validationErrors.email}</div>}
            </div>
          </div>

          {/* Contact Number and Alternative Number */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle} htmlFor="contactNumber">Contact Number:</label>
              <input
                id="contactNumber"
                type="tel"
                value={formData.contactNumber || ""}
                onChange={handleContactNumberChange}
                style={{
                  ...inputStyle,
                  ...(validationErrors.contactNumber && errorInputStyle),
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                }}
                placeholder="+91 1234567890"
              />
              {validationErrors.contactNumber && <div style={errorTextStyle}>{validationErrors.contactNumber}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle} htmlFor="altNumber">Alternative Number:</label>
              <input
                id="altNumber"
                type="tel"
                value={formData.altNumber || ""}
                onChange={handleAltNumberChange}
                style={{
                  ...inputStyle,
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                }}
                placeholder="+91 9876543210"
              />
            </div>
          </div>

          {/* Classes Checkbox */}
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Select Classes:</label>
            <div style={checkboxContainerStyle}>
              {proposedClassesList.length > 0 ? (
                proposedClassesList.map((cls, index) => (
                  <div key={index} style={checkboxItemStyle}>
                    <input
                      type="checkbox"
                      id={`class-${cls.id}`}
                      value={cls.id}
                      checked={selectedClasses.includes(cls.id)}
                      onChange={handleSelectedClassesChange}
                      style={checkboxInputStyle}
                    />
                    <label htmlFor={`class-${cls.id}`} style={{ fontWeight: 'normal', marginBottom: '0px' }}>
                      {cls.class_name}
                    </label>
                  </div>
                ))
              ) : (
                <p style={{ color: "#777", fontSize: "13px" }}>No classes available.</p>
              )}
            </div>
          </div>

          {/* Seasons */}
          <div className="d-flex flex-column gap-3 seasonContainerStyle1" style={{ marginBottom: "20px" }}>
            {["summer", "winter"].map((season) => {
              const otherSeason = season === "summer" ? "winter" : "summer";
              const otherSeasonMonths = formData[otherSeason].months || [];
              return (
              <div className="seasonContainerStyle" key={season}  >
                <div style={sectionHeaderStyle}>
                  {season.charAt(0).toUpperCase() + season.slice(1)} Timings
                </div>

                {/* Months */}
                <div style={{ marginBottom: "15px" }}>
                  <label style={labelStyle}>Select Months:</label>
                  <div style={checkboxContainerStyle}>
                    {MONTHS.map((month) => {
                      const isTakenByOtherSeason = otherSeasonMonths.includes(month.value);
                      return (
                      <div key={month.value} style={checkboxItemStyle}>
                        <input
                          type="checkbox"
                          id={`${season}-month-${month.value}`}
                          checked={(formData[season].months || []).includes(month.value)}
                          onChange={() => handleMonthToggle(season, month.value)}
                          disabled={isTakenByOtherSeason}
                          style={checkboxInputStyle}
                        />
                        <label
                          htmlFor={`${season}-month-${month.value}`}
                          style={{
                            fontWeight: "normal",
                            marginBottom: "0px",
                            color: isTakenByOtherSeason ? "#aaa" : undefined,
                          }}
                        >
                          {month.label}
                        </label>
                      </div>
                      );
                    })}
                  </div>
                </div>

                {/* School Start and End */}
                <div>
                  <label style={labelStyle}>School Start and End:</label>
                  <div style={flexRowStyle}>
                    <input
                      type="time"
                      value={formData[season].schoolStart || ""}
                      onChange={(e) => handleInputChange(season, "schoolStart", e.target.value)}
                      style={{ ...inputStyle, ...(validationErrors[`${season}School`] && errorInputStyle) }}
                      required
                    />
                    <span style={{ fontWeight: "600" }}>to</span>
                    <input
                      type="time"
                      value={formData[season].schoolEnd || ""}
                      onChange={(e) => handleInputChange(season, "schoolEnd", e.target.value)}
                      style={{ ...inputStyle, ...(validationErrors[`${season}School`] && errorInputStyle) }}
                      required
                    />
                  </div>
                  {validationErrors[`${season}School`] && <div style={errorTextStyle}>{validationErrors[`${season}School`]}</div>}
                </div>

                {/* Assembly Start and End */}
                <div>
                  <label style={labelStyle}>Assembly Start and End:</label>
                  <div style={flexRowStyle}>
                    <input
                      type="time"
                      value={formData[season].assemblyStart || ""}
                      onChange={(e) => handleInputChange(season, "assemblyStart", e.target.value)}
                      style={{ ...inputStyle, ...(validationErrors[`${season}Assembly`] && errorInputStyle) }}
                      required
                    />
                    <span style={{ fontWeight: "600" }}>to</span>
                    <input
                      type="time"
                      value={formData[season].assemblyEnd || ""}
                      onChange={(e) => handleInputChange(season, "assemblyEnd", e.target.value)}
                      style={{ ...inputStyle, ...(validationErrors[`${season}Assembly`] && errorInputStyle) }}
                      required
                    />
                  </div>
                  {validationErrors[`${season}Assembly`] && <div style={errorTextStyle}>{validationErrors[`${season}Assembly`]}</div>}
                </div>

                {/* Break Slots Count */}
                <div style={{ marginBottom: "15px" }}>
                  <label style={labelStyle}>Number of Break Slots:</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={(formData[season].breakTimings || []).length}
                    onChange={(e) => handleBreakSlotsCountChange(season, e)}
                    style={{ ...inputStyle, width: "80px", height: "36px", fontSize: "14px" }}
                  />
                </div>

                {/* Break Timings */}
                {(formData[season].breakTimings || []).length > 0 && (
                  <div>
                    <label style={labelStyle}>Break Timings:</label>
                    {formData[season].breakTimings.map((breakTime, index) => (
                      <div key={index} style={{ marginBottom: "10px" }}>
                        <div style={{ fontSize: "13px", marginBottom: "4px" }}>
                          Break {index + 1}:
                        </div>
                        <div style={flexRowStyle}>
                          <input
                            type="time"
                            value={breakTime.start || ""}
                            onChange={(e) => handleBreakTimeChange(season, index, "start", e.target.value)}
                            style={{ ...inputStyle, ...(validationErrors[`${season}Break${index}`] && errorInputStyle) }}
                          />
                          <span style={{ fontWeight: "600" }}>to</span>
                          <input
                            type="time"
                            value={breakTime.end || ""}
                            onChange={(e) => handleBreakTimeChange(season, index, "end", e.target.value)}
                            style={{ ...inputStyle, ...(validationErrors[`${season}Break${index}`] && errorInputStyle) }}
                          />
                        </div>
                        {validationErrors[`${season}Break${index}`] && <div style={errorTextStyle}>{validationErrors[`${season}Break${index}`]}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {/* File Uploads (Logo and Headers) */}
          <div className="d-flex school-crt-form mt-3 gap-3" >
            <div style={{ flex: 1 }}>
              <label style={labelStyle} htmlFor="schoolLogo">School Logo:</label>
              <input
                id="schoolLogo"
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                style={{ padding: "6px", border: "1px solid #ccc", borderRadius: "6px", width: "100%" }}
              />
              {formData.logo && (
                <div style={{ marginTop: "8px" }}>
                  {typeof formData.logo === 'string' ? (
                    <a href={formData.logo} target="_blank" rel="noopener noreferrer" style={{ color: "#007BFF", textDecoration: "none", fontSize: "14px", display: "inline-flex", alignItems: "center" }}>
                      👁️ View Current Logo
                    </a>
                  ) : (
                    <span style={{ fontSize: "14px", color: "#555" }}>✅ {formData.logo.name} selected</span>
                  )}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle} htmlFor="headerDocs">Header Documents (Multiple Files):</label>
              <input
                id="headerDocs"
                type="file"
                multiple
                onChange={handleHeaderFilesChange}
                style={{ padding: "6px", border: "1px solid #ccc", borderRadius: "6px", width: "100%" }}
              />
              {formData.headers && formData.headers.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {formData.headers.map((fileOrUrl, index) => (
                    <div key={index} style={{ marginBottom: "4px" }}>
                      {typeof fileOrUrl === 'string' ? (
                        <a href={fileOrUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#007BFF", textDecoration: "none", fontSize: "14px", display: "inline-flex", alignItems: "center" }}>
                          👁️ View Header File {index + 1}
                        </a>
                      ) : (
                        <span style={{ fontSize: "14px", color: "#555" }}>✅ {fileOrUrl.name} selected</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit/Update Button */}
          <div style={{ marginTop: "25px" }}>
            {formData.isUpdate ? (
              <button
                type="button"
                style={{
                  padding: "10px 25px",
                  backgroundColor: "#007BFF",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: (formData.loading || !hasChanges) ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: (formData.loading || !hasChanges) ? 0.7 : 1,
                }}
                onClick={handleUpdate}
                disabled={formData.loading || !hasChanges}
              >
                {formData.loading ? "Updating..." : "Update"}
              </button>
            ) : (
              <button
                type="submit"
                style={{
                  padding: "10px 25px",
                  backgroundColor: "#007BFF",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: formData.loading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: formData.loading ? 0.7 : 1,
                }}
                disabled={formData.loading}
              >
                {formData.loading ? "Submitting..." : "Submit"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMaster;